import { validateSSN, validateDOBByRelationship, validatePhone } from '@/shared/utils'

// Helper function to calculate age from DOB
const calculateAge = (dob: string): number => {
  const birthDate = new Date(dob)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

// Validation function for Step3
export const validateStep3 = (data: any) => {
  const errors: { [key: string]: string } = {}
  
  if (!data.agentName) errors.agentName = 'Agent Name is required'
  if (!data.relationToVictim) errors.relationToVictim = 'Relation to individual is required'
 
  if (data.isClaimantDeceased === undefined || data.isClaimantDeceased === null) errors.isClaimantDeceased = 'Claimant deceased status is required'
  if (data.isClaimantDeceased === true && !data.dateOfDeath) errors.dateOfDeath = 'Date of Death is required'
  if (data.isMinor === undefined || data.isMinor === null) errors.isMinor = 'Minor status is required'
  if (!data.injuredPartyFirstName) errors.injuredPartyFirstName = 'Injured Party First Name is required'
  if (!data.injuredPartyLastName) errors.injuredPartyLastName = 'Injured Party Last Name is required'
  
  // Validate Video Gamer fields for roblox campaign when "Loved One" is selected (moved from Step 0)
  const isRobloxCampaign = data.campaignId === 'roblox-exploitation' ||
    data.selectedCampaign === 'robloxExploitation' ||
    data.campaign?.name?.toLowerCase().includes('roblox')
  
  if (isRobloxCampaign && data.relationToVictim === 'Loved one') {
    if (!data.callerFirstName) errors.callerFirstName = 'Video Gamer First Name is required'
    if (!data.callerLastName) errors.callerLastName = 'Video Gamer Last Name is required'
  }
  
  if (!data.gender) {
    errors.gender = 'Gender is required'
  } else {
    // Campaign gender eligibility validation
    const campaign = data.campaign
    if (campaign?.allowedGenders && campaign.allowedGenders.length > 0) {
      const isGenderAllowed = campaign.allowedGenders.includes(data.gender)
      if (!isGenderAllowed) {
        const allowedGendersList = campaign.allowedGenders
          .map((g: string) => g === 'ALL_GENDERS' ? 'All Genders' : g.charAt(0) + g.slice(1).toLowerCase())
          .join(', ')
        errors.gender = `This campaign only accepts: ${allowedGendersList}`
      }
    }
  }
  if (!data.injuredPartyEmail) errors.injuredPartyEmail = 'Injured Party Email is required'
  if (!data.injuredPartyAddressLine1) errors.injuredPartyAddressLine1 = 'Address Line 1 is required'
  if (!data.injuredPartyCity) errors.injuredPartyCity = 'City is required'
  if (!data.injuredPartyState) errors.injuredPartyState = 'State is required'
  if (!data.injuredPartyZipCode) errors.injuredPartyZipCode = 'ZIP Code is required'
  if (!data.phoneNumber) {
    errors.phoneNumber = 'Phone Number is required'
  } else {
    const phoneValidation = validatePhone(data.phoneNumber)
    if (!phoneValidation.isValid) {
      errors.phoneNumber = phoneValidation.error || 'Phone number must be a valid American phone number'
    }
  }
  if (!data.callerNumberType) errors.callerNumberType = 'Caller Number Type is required'
  if (!data.injuredPartyDOB) {
    errors.injuredPartyDOB = 'Injured Party DOB is required'
  } else {
    // Validate DOB based on relationship to victim and campaign settings
    const dobValidation = validateDOBByRelationship(data.injuredPartyDOB, data.relationToVictim, data.campaign)
    if (!dobValidation.isValid && dobValidation.errorMessage) {
      errors.injuredPartyDOB = dobValidation.errorMessage
    }

    // Campaign age eligibility validation (additional check for range validation)
    const campaign = data.campaign
    if (campaign && (campaign.minAge !== null || campaign.maxAge !== null)) {
      const clientAge = calculateAge(data.injuredPartyDOB)
      const hasMinAge = campaign.minAge !== null && campaign.minAge !== undefined
      const hasMaxAge = campaign.maxAge !== null && campaign.maxAge !== undefined
      
      // Check if client age is outside the allowed range
      if (hasMinAge && clientAge < campaign.minAge) {
        if (hasMaxAge) {
          errors.injuredPartyDOB = `Client must be between ${campaign.minAge} and ${campaign.maxAge} years old for this campaign (current age: ${clientAge})`
        } else {
          errors.injuredPartyDOB = `Client must be at least ${campaign.minAge} years old for this campaign (current age: ${clientAge})`
        }
      } else if (hasMaxAge && clientAge > campaign.maxAge) {
        if (hasMinAge) {
          errors.injuredPartyDOB = `Client must be between ${campaign.minAge} and ${campaign.maxAge} years old for this campaign (current age: ${clientAge})`
        } else {
          errors.injuredPartyDOB = `Client must be ${campaign.maxAge} years old or younger for this campaign (current age: ${clientAge})`
        }
      }
    }
  }
  
  // SSN validation
  if (!data.ssn) {
    errors.ssn = 'SSN is required'
  } else {
    const ssnValidation = validateSSN(data.ssn)
    if (!ssnValidation.isValid && ssnValidation.error) {
      errors.ssn = ssnValidation.error
    }
  }
  
  if (data.hasAttorneyRepresentation === undefined || data.hasAttorneyRepresentation === null) {
    errors.hasAttorneyRepresentation = 'Attorney representation status is required'
  } else if (isRobloxCampaign && data.hasAttorneyRepresentation === true) {
    // For roblox campaigns, selecting "Yes" disqualifies the client and prevents continue
    errors.hasAttorneyRepresentation = 'DQ: Client has previously signed documents with an attorney - cannot proceed'
  }
  
  return errors
}
